import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { generateCycleCountLines } from "@/lib/inventory.functions";
import { getLocationDetail, startCycleCountForLocation } from "@/lib/inventory-views.functions";
import { runServerFn } from "@/lib/server-fn";
import { PageHeader } from "@/components/operator-shell";
import { HelpButton } from "@/components/kb/help-button";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, ClipboardCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/inventory/locations/$locationId")({ component: LocationDetailPage });

function LocationDetailPage() {
  const { locationId } = Route.useParams();
  const navigate = useNavigate();
  const generateLinesFn = useServerFn(generateCycleCountLines);
  const getDetailFn = useServerFn(getLocationDetail);
  const startCountFn = useServerFn(startCycleCountForLocation);
  const [starting, setStarting] = useState(false);

  const { data: bundle } = useQuery({
    queryKey: ["location-detail", locationId],
    queryFn: () => getDetailFn({ data: { locationId } }),
  });
  const loc = bundle?.location;
  const rows = bundle?.balances ?? [];
  const skus = bundle?.skus ?? [];
  const skuMap = Object.fromEntries(skus.map((s) => [s.id, s]));
  const occupied = rows.length > 0;

  const startCount = async () => {
    if (!loc) return;
    setStarting(true);
    try {
      const cc = await startCountFn({ data: { locationId: loc.id } });
      await runServerFn(
        () => generateLinesFn({ data: { cycleCountId: cc.id, locationIds: [loc.id] } }),
        "Generate count lines",
      );
      toast.success("Cycle count created");
      navigate({ to: "/app/cycle-counts/$id", params: { id: cc.id } });
    } catch (err) {
      toast.error((err as Error).message);
    } finally { setStarting(false); }
  };

  return (
    <>
      <PageHeader
        title={loc?.code ?? "Location"}
        description={loc ? [loc.type, loc.zone && `zone ${loc.zone}`, loc.aisle && `aisle ${loc.aisle}`].filter(Boolean).join(" · ") : undefined}
        actions={
          <div className="flex items-center gap-2">
            <HelpButton moduleKey="inventory.locations" />
            <Button variant="outline" onClick={() => navigate({ to: "/app/inventory/locations" })}>
              <ArrowLeft className="h-4 w-4 mr-2" />All locations
            </Button>
            <Button onClick={startCount} disabled={starting}>
              {starting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ClipboardCheck className="h-4 w-4 mr-2" />}
              Start cycle count here
            </Button>
          </div>
        }
      />
      <div className="p-4 sm:p-6 space-y-4">
        <div className="flex flex-wrap gap-2">
          <Badge variant={occupied ? "default" : "outline"}>{occupied ? "Occupied" : "Available"}</Badge>
          <Badge variant={loc?.active ? "secondary" : "outline"}>{loc?.active ? "Active" : "Inactive"}</Badge>
          <Badge variant="secondary">{rows.length} SKU lot(s)</Badge>
        </div>
        <div className="border rounded-lg bg-card">
          <Table>
            <TableHeader><TableRow><TableHead>SKU</TableHead><TableHead>Lot</TableHead><TableHead className="text-right">Qty on hand</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={3} className="text-center py-10 text-muted-foreground">
                  No inventory in this location — Available.
                </TableCell></TableRow>
              ) : rows.map((r, i) => {
                const s = r.sku_id ? skuMap[r.sku_id] : null;
                return (
                  <TableRow key={i}>
                    <TableCell>{s ? <><span className="font-mono text-xs">{s.sku_code}</span> <span className="text-muted-foreground text-xs ml-1">{s.name}</span></> : "—"}</TableCell>
                    <TableCell className="text-muted-foreground text-xs">{r.lot ?? "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.qty_on_hand}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        <p className="text-xs text-muted-foreground">
          <Link to="/app/inventory/on-hand" className="underline">View all on-hand</Link>
        </p>
      </div>
    </>
  );
}